import { tx } from "@/i18n/tx";
import { useEffect, useState, useRef } from "react";
import { api, API_BASE, fmtCHF, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import {
  Landmark, Upload, Plus, ChevronRight, CheckCircle2, AlertTriangle,
  X, Loader2, FileText, Search, ArrowLeft, EyeOff,
} from "lucide-react";

const CONFIDENCE_BADGE = {
  strong: { label: tx("ui_banque.k677a59"), cls: "bg-primary-c/15 text-primary-c" },
  probable: { label: tx("ui_banque.kd245ed"), cls: "bg-amber-100 text-amber-700" },
  fuzzy: { label: tx("ui_banque.k08afaf"), cls: "bg-blue-100 text-blue-700" },
  grouped: { label: tx("ui_banque.ka5cafe"), cls: "bg-purple-100 text-purple-700" },
};

const STATUS_BADGE = {
  matched: { label: tx("ui_banque.k16bf04"), cls: "bg-primary-c/15 text-primary-c" },
  suggested: { label: "🔎 Suggestion", cls: "bg-amber-100 text-amber-700" },
  unmatched: { label: tx("ui_banque.k04b377"), cls: "bg-surface-muted text-text-secondary" },
  ignored: { label: tx("ui_banque.ka9ab30"), cls: "bg-gray-100 text-gray-600" },
};

function ManualMatchDrawer({ tx, onClose, onConfirm }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/bank/invoices/open${q ? `?q=${encodeURIComponent(q)}` : ""}`);
        if (!cancelled) setItems(r.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const selectedTotal = items.filter((i) => selected[i.id]).reduce((a, b) => a + b.amount, 0);
  const txAmount = Math.abs(tx.amount);
  const diff = selectedTotal - txAmount;
  const isPartial = Math.abs(diff) > 0.02;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="manual-match-drawer">
        <div className="p-4 border-b border-c flex items-center justify-between">
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{tx.label}</p>
            <p className="text-xs text-text-secondary">{fmtCHF(Math.abs(tx.amount))} · {fmtDate(tx.date_value)}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-surface-muted"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 border-b border-c">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tx("ui_banque.kc0ddcb")}
              data-testid="manual-match-search"
              className="input-c pl-9"
              autoFocus
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {loading && <p className="text-center text-sm text-text-secondary py-4"><Loader2 className="w-4 h-4 animate-spin inline" /></p>}
          {!loading && items.length === 0 && <p className="text-center text-sm text-text-secondary py-6">{tx("ui_banque.k31add4")}</p>}
          {items.map((i) => {
            const on = !!selected[i.id];
            return (
              <button
                key={i.id}
                onClick={() => toggle(i.id)}
                data-testid={`match-inv-${i.id}`}
                className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 transition-colors ${on ? "border-primary-c bg-primary-c/5" : "border-c hover:border-primary-c/50"}`}
              >
                <div className={`w-5 h-5 rounded-md border-2 flex-none mt-0.5 flex items-center justify-center ${on ? "bg-primary-c border-primary-c text-white" : "border-c"}`}>
                  {on && <CheckCircle2 className="w-3.5 h-3.5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{i.supplier}</p>
                  <p className="text-[11px] text-text-secondary mt-0.5">Échéance {fmtDate(i.due_date)}</p>
                </div>
                <p className="text-sm font-mono shrink-0">{fmtCHF(i.amount)}</p>
              </button>
            );
          })}
        </div>
        {selectedIds.length > 0 && (
          <div className="p-4 border-t border-c bg-surface-muted">
            <div className="flex items-center justify-between text-xs mb-2">
              <span>{tx("ui_banque.k4f68bd")} <strong>{fmtCHF(selectedTotal)}</strong> · {tx("r3.transaction")} <strong>{fmtCHF(txAmount)}</strong></span>
              {isPartial && <span className="text-amber-700">Δ {fmtCHF(diff)}</span>}
            </div>
            <button
              data-testid="confirm-match-btn"
              onClick={() => onConfirm(selectedIds, isPartial)}
              className="btn-primary w-full"
            >
              {isPartial ? "Confirmer paiement partiel" : `Confirmer le rapprochement (${selectedIds.length})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TransactionCard({ tx, onConfirmSuggestion, onOpenManual, onIgnore, busy }) {
  const isDebit = tx.amount < 0;
  const badge = STATUS_BADGE[tx.status] || STATUS_BADGE.unmatched;
  const cbadge = tx.match_confidence ? CONFIDENCE_BADGE[tx.match_confidence] : null;

  return (
    <div className="card-c" data-testid={`tx-${tx.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
            {cbadge && <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${cbadge.cls}`}>{cbadge.label}</span>}
          </div>
          <p className="text-sm font-medium mt-1.5 truncate">{tx.label || "—"}</p>
          <p className="text-[11px] text-text-secondary">{fmtDate(tx.date_value)} · {tx.currency}</p>
        </div>
        <p className={`text-sm font-mono shrink-0 ${isDebit ? "text-red-600" : "text-primary-c"}`}>
          {isDebit ? "" : "+"}{fmtCHF(tx.amount)}
        </p>
      </div>

      {tx.status === "suggested" && tx.suggested_invoices?.length > 0 && (
        <div className="mt-3 border-t border-c pt-3 space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold">{tx("ui_banque.kfb4fab")}</p>
          {tx.suggested_invoices.map((inv) => (
            <div key={inv.invoice_id} className="flex items-center justify-between gap-2 text-xs bg-surface-muted rounded-lg p-2">
              <div className="min-w-0">
                <p className="font-medium truncate">{inv.supplier}</p>
                <p className="text-[10px] text-text-secondary">{fmtCHF(inv.amount)} · {fmtDate(inv.due_date)}</p>
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => onConfirmSuggestion(tx)}
              disabled={busy}
              data-testid={`tx-confirm-${tx.id}`}
              className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              {tx("ui_banque.k303a74")}
            </button>
            <button
              onClick={() => onOpenManual(tx)}
              data-testid={`tx-manual-${tx.id}`}
              className="text-xs px-3 py-1.5 rounded-lg border border-c bg-white text-text-secondary hover:border-primary-c/50"
            >
              {tx("ui_banque.ke07906")}
            </button>
          </div>
        </div>
      )}

      {(tx.status === "unmatched" || (tx.status === "suggested" && !tx.suggested_invoices?.length)) && isDebit && (
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={() => onOpenManual(tx)}
            data-testid={`tx-manual-${tx.id}`}
            className="text-xs px-3 py-1.5 rounded-lg border border-c bg-white text-text-secondary hover:border-primary-c/50 inline-flex items-center gap-1"
          >
            <Search className="w-3.5 h-3.5" />
            {tx("ui_banque.k8f3239")}
          </button>
          <button
            onClick={() => onIgnore(tx)}
            disabled={busy}
            data-testid={`tx-ignore-${tx.id}`}
            className="text-xs px-3 py-1.5 rounded-lg border border-c bg-white text-text-secondary hover:border-primary-c/50 inline-flex items-center gap-1"
          >
            <EyeOff className="w-3.5 h-3.5" />
            {tx("ui_banque.k27b5ec")}
          </button>
        </div>
      )}
    </div>
  );
}

function StatementDetail({ stmtId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manual, setManual] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/bank/statements/${stmtId}`);
      setData(r.data);
    } catch { toast.error(tx("ui_banque.k2ec40f")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [stmtId]);

  const doMatch = async (tx, invoiceIds, partial = false) => {
    setBusy(true);
    try {
      await api.post(`/bank/transactions/${tx.id}/match`, { invoice_ids: invoiceIds, partial });
      toast.success(tx("ui_banque.k8e43fa"));
      setManual(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tx("ui_banque.kfe7d2c"));
    } finally { setBusy(false); }
  };

  const confirmSuggestion = (tx) => {
    const ids = (tx.suggested_invoices || []).map((s) => s.invoice_id);
    if (!ids.length) return;
    const partial = tx.match_confidence === "probable" || tx.match_confidence === "fuzzy";
    doMatch(tx, ids, partial && Math.abs(tx.suggested_invoices.reduce((a, b) => a + b.amount, 0) - Math.abs(tx.amount)) > 0.02);
  };

  const ignoreTx = async (tx) => {
    setBusy(true);
    try {
      await api.post(`/bank/transactions/${tx.id}/ignore`);
      toast.success(tx("ui_banque.kb39452"));
      load();
    } catch { toast.error(tx("ui_banque.kfe7d2c")); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!data) return null;

  const counts = data.transactions.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <button onClick={onBack} className="text-sm text-text-secondary hover:text-primary-c flex items-center gap-1 mb-3" data-testid="stmt-back">
        <ArrowLeft className="w-4 h-4" /> {tx("ui_banque.k6cd1f1")}
      </button>
      <div className="card-c mb-4">
        <p className="text-[10px] uppercase tracking-wider text-text-secondary font-bold">{tx("ui_banque.kb5be3c")}</p>
        <p className="font-semibold text-sm mt-1 truncate">{data.filename}</p>
        <p className="text-xs text-text-secondary mt-0.5">{fmtDate(data.period_start)} → {fmtDate(data.period_end)}</p>
        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
          <div className="bg-primary-c/10 rounded-lg py-2">
            <p className="text-lg font-display leading-none text-primary-c">{counts.matched || 0}</p>
            <p className="text-[9px] uppercase text-text-secondary mt-1">{tx("ui_banque.kf23cc2")}</p>
          </div>
          <div className="bg-amber-100 rounded-lg py-2">
            <p className="text-lg font-display leading-none text-amber-700">{counts.suggested || 0}</p>
            <p className="text-[9px] uppercase text-text-secondary mt-1">{tx("ui_banque.k41ecca")}</p>
          </div>
          <div className="bg-surface-muted rounded-lg py-2">
            <p className="text-lg font-display leading-none">{counts.unmatched || 0}</p>
            <p className="text-[9px] uppercase text-text-secondary mt-1">{tx("ui_banque.ka62ee5")}</p>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {data.transactions.map((t) => (
          <TransactionCard
            key={t.id}
            tx={t}
            busy={busy}
            onConfirmSuggestion={confirmSuggestion}
            onOpenManual={(x) => setManual(x)}
            onIgnore={ignoreTx}
          />
        ))}
      </div>
      {manual && (
        <ManualMatchDrawer
          tx={manual}
          onClose={() => setManual(null)}
          onConfirm={(ids, partial) => doMatch(manual, ids, partial)}
        />
      )}
    </>
  );
}

export default function Banque() {
  const [accounts, setAccounts] = useState([]);
  const [statements, setStatements] = useState([]);
  const [newAcc, setNewAcc] = useState(false);
  const [accName, setAccName] = useState("");
  const [accBank, setAccBank] = useState("");
  const [accIban, setAccIban] = useState("");
  const [uploadAcc, setUploadAcc] = useState("");
  const [uploading, setUploading] = useState(false);
  const [activeStmt, setActiveStmt] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [a, s] = await Promise.all([api.get("/bank/accounts"), api.get("/bank/statements")]);
      setAccounts(a.data || []);
      setStatements(s.data || []);
      if (!uploadAcc && a.data?.length) setUploadAcc(a.data[0].id);
    } catch { toast.error(tx("ui_banque.k81b5bb")); }
  };
  useEffect(() => { load(); }, []);

  const createAccount = async () => {
    if (!accName.trim()) { toast.error(tx("r3.name_required")); return; }
    try {
      await api.post("/bank/accounts", { name: accName, bank: accBank, iban: accIban, currency: "CHF" });
      toast.success(tx("ui_banque.k417443"));
      setAccName(""); setAccBank(""); setAccIban(""); setNewAcc(false);
      load();
    } catch { toast.error(tx("ui_banque.kfe7d2c")); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!uploadAcc) { toast.error(tx("ui_banque.kfabb1e")); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("bank_account_id", uploadAcc);
    try {
      const r = await fetch(`${API_BASE}/bank/statements/upload`, { method: "POST", credentials: "include", body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || tx("ui_banque.kfe7d2c"));
      toast.success(`✓ ${tx("tpl.imported", { n: j.imported })}${j.skipped ? ` · ${tx("tpl.skipped", { n: j.skipped })}` : ""}`);
      load();
      setActiveStmt(j.statement_id);
    } catch (e) {
      toast.error(typeof e.message === "string" ? e.message : tx("ui_banque.k56e68c"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (activeStmt) return <StatementDetail stmtId={activeStmt} onBack={() => { setActiveStmt(null); load(); }} />;

  return (
    <div className="space-y-5" data-testid="banque-page">
      <div>
        <h1 className="font-display text-3xl">{tx("ui_banque.kf0321a")} <span className="font-serif-italic text-primary-c">bancaires.</span></h1>
        <p className="text-text-secondary text-sm mt-1">{tx("ui_banque.kcd717f")}</p>
      </div>

      {/* Comptes bancaires */}
      <div className="card-c">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold">{tx("ui_banque.k8d0d4f")}</p>
          <button onClick={() => setNewAcc(!newAcc)} className="text-xs text-primary-c font-semibold" data-testid="new-account-btn">
            {newAcc ? tx("ui_banque.kd12c3b") : tx("ui_banque.k41b6c5")}
          </button>
        </div>
        {newAcc && (
          <div className="mt-3 space-y-2">
            <input value={accName} onChange={(e) => setAccName(e.target.value)} placeholder={tx("ui_banque.kffdd89")} className="input-c" data-testid="acc-name-input" />
            <input value={accBank} onChange={(e) => setAccBank(e.target.value)} placeholder={tx("ui_banque.ka2e702")} className="input-c" />
            <input value={accIban} onChange={(e) => setAccIban(e.target.value)} placeholder={tx("ui_banque.kd79a6a")} className="input-c" />
            <button onClick={createAccount} className="btn-primary w-full text-sm" data-testid="acc-create-btn">{tx("ui_banque.k6096f7")}</button>
          </div>
        )}
        <div className="mt-3 space-y-1">
          {accounts.length === 0 && !newAcc && <p className="text-xs text-text-secondary">{tx("ui_banque.kd7c0f6")}</p>}
          {accounts.map((a) => (
            <div key={a.id} className="flex items-center gap-3 py-2 border-b border-c last:border-0" data-testid={`acc-${a.id}`}>
              <Landmark className="w-4 h-4 text-primary-c shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{a.name}</p>
                <p className="text-[10px] text-text-secondary">{a.bank || "—"} · {a.iban || "—"}</p>
              </div>
              <span className="text-[10px] text-text-secondary">{a.currency}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Upload */}
      {accounts.length > 0 && (
        <div className="card-c">
          <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold">{tx("ui_banque.k15d301")}</p>
          <div className="mt-3 space-y-2">
            <select value={uploadAcc} onChange={(e) => setUploadAcc(e.target.value)} className="input-c" data-testid="upload-acc-select">
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <input ref={fileRef} type="file" accept=".csv,.txt,.pdf,text/csv,application/pdf" onChange={onFile} className="hidden" data-testid="upload-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="upload-btn"
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-c rounded-xl py-6 text-sm text-text-secondary hover:border-primary-c hover:text-primary-c transition-colors disabled:opacity-50"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              {uploading ? "Traitement…" : tx("ui_banque.k373e1e")}
            </button>
            <p className="text-[10px] text-text-secondary">{tx("ui_banque.k562484")}</p>
          </div>
        </div>
      )}

      {/* Historique */}
      <div>
        <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-2">{tx("ui_banque.ked4357")}</p>
        {statements.length === 0 && (
          <div className="card-c text-center py-8">
            <FileText className="w-8 h-8 text-text-secondary mx-auto" strokeWidth={1.5} />
            <p className="text-sm text-text-secondary mt-2">{tx("ui_banque.ke826f1")}</p>
          </div>
        )}
        <div className="space-y-2">
          {statements.map((s) => {
            const acc = accounts.find((a) => a.id === s.bank_account_id);
            const cnt = s.counts_by_status || {};
            return (
              <button
                key={s.id}
                onClick={() => setActiveStmt(s.id)}
                data-testid={`stmt-${s.id}`}
                className="card-c w-full text-left flex items-center justify-between gap-3 hover:border-primary-c/50 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{s.filename}</p>
                  <p className="text-[10px] text-text-secondary mt-0.5">{acc?.name || "—"} · {fmtDate(s.period_start)} → {fmtDate(s.period_end)}</p>
                  <div className="mt-2 flex items-center gap-1.5 text-[10px]">
                    <span className="px-1.5 py-0.5 rounded-full bg-primary-c/10 text-primary-c font-semibold">{cnt.matched?.n || 0} ✓</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{cnt.suggested?.n || 0} suggestions</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-surface-muted text-text-secondary font-semibold">{cnt.unmatched?.n || 0} à traiter</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-text-secondary shrink-0" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
